import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useLocation,
  useMatch,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { renderAssetTypePlaceholder } from '~/constants/assetTypeImages';
import {
  Status,
  useAssetQuery,
  useUpdateAssetMutation,
} from '~/generated/graphql';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { SideLayout } from '~/layouts/side/SideLayout';
import { FloatingActionButton } from '../FloatingActionButton';
import { AttributesList } from '../sites/tabs/AttributesList';
import { AssetViewSkeleton } from '../skeletons';
import { InfoCard } from '../ui/InfoCard/InfoCard';
import { ReadValue } from '../ui/ReadValue';
import { TabHeader } from '../ui/TabHeader';
import { AssetMenu } from './AssetMenu';
import { buildAssetViewTabs } from './assetViewTabs';
import { AssetHistory } from './tabs/AssetHistory';
import { AssetJobs, useAssetJobs } from './tabs/AssetJobs';

// Components are imported directly for better performance

// Constants for timing and delays
const TIMING = {
  JOBS_LOAD_DELAY: 100,
  HISTORY_LOAD_DELAY: 500,
  TAB_SWITCH_DELAY: 100,
  HIGHLIGHT_DURATION: 3100,
} as const;

/**
 * Sharing permissions for external users viewing assets from job tasks.
 * When provided, these control which tabs are visible to external assignees.
 * When not provided (undefined), all tabs are visible (internal user or direct asset access).
 */
type SharingPermissions = {
  shareAttributes?: boolean;
  shareJobHistory?: boolean;
  shareDeploymentHistory?: boolean;
};

type Props = {
  id?: string;
  isPopup?: boolean;
  /**
   * Job ID when viewing asset from a task context. Used by backend to verify
   * task-level access permissions for external users.
   */
  jobId?: string;
  /**
   * Task ID when viewing asset from a task context. Used by backend to get
   * the specific task's sharing configuration (takes precedence over jobId).
   */
  taskId?: string;
  /**
   * When viewing an asset from a job task context, these permissions control
   * what external users can see. Internal users always have full access.
   */
  sharingPermissions?: SharingPermissions;
};

// Custom hook to manage asset tab state with sessionStorage
const useAssetTabState = (assetId: string) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get the storage key for this specific asset
  const storageKey = `assetTab_${assetId}`;

  // Get initial tab from URL or sessionStorage
  const getInitialTab = useCallback(() => {
    // First priority: URL parameter
    const urlParams = new URLSearchParams(location.search);
    const urlTab = urlParams.get('tab');
    if (urlTab) {
      const tabIndex = parseInt(urlTab, 10);
      if (!isNaN(tabIndex)) {
        // Store in sessionStorage when found in URL
        sessionStorage.setItem(storageKey, tabIndex.toString());
        return tabIndex;
      }
    }

    // Second priority: sessionStorage
    const storedTab = sessionStorage.getItem(storageKey);
    if (storedTab) {
      const tabIndex = parseInt(storedTab, 10);
      if (!isNaN(tabIndex)) {
        return tabIndex;
      }
    }

    // Default: first tab
    return 0;
  }, [location.search, storageKey]);

  // Update tab state and persist to sessionStorage
  const setTabIndex = useCallback(
    (index: number) => {
      // Store in sessionStorage
      sessionStorage.setItem(storageKey, index.toString());

      // Update URL if we're on the main asset page (not in a sub-route)
      const isMainAssetPage = location.pathname === `/assets/${assetId}`;
      if (isMainAssetPage) {
        const currentSearch = new URLSearchParams(location.search);
        if (index === 0) {
          currentSearch.delete('tab');
        } else {
          currentSearch.set('tab', index.toString());
        }

        const searchString = currentSearch.toString();
        const newPath =
          location.pathname + (searchString ? `?${searchString}` : '');
        navigate(newPath, { replace: true });
      }
    },
    [assetId, location.pathname, location.search, navigate, storageKey]
  );

  return {
    getInitialTab,
    setTabIndex,
  };
};

export function AssetView({
  id,
  isPopup,
  jobId: jobIdProp,
  taskId: taskIdProp,
  sharingPermissions,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    siteId,
    assetId,
    jobId: jobIdFromParams,
    taskId: taskIdFromParams,
  } = useParams();
  const matchSites = useMatch('/sites/*');
  const matchAssets = useMatch('/assets/*');
  const { isMobile } = useBreakpoint();

  // Get jobId from props (when viewing from task popup) or URL params (when viewing from job route)
  const jobId = jobIdProp || jobIdFromParams;

  // Get taskId from props (when viewing from task popup) or URL params (when viewing from job route)
  const taskId = taskIdProp || taskIdFromParams;

  // CRITICAL FIX: Track if component is mounted to prevent async state updates after unmount
  const isMountedRef = useRef(true);

  // Memoize the effective asset ID to prevent unnecessary re-renders
  const effectiveAssetId = useMemo(
    () => (isPopup ? id : assetId),
    [isPopup, id, assetId]
  );

  // Use cache-first for initial load to prevent blinking, but allow background updates
  const [{ data: assetData, fetching }] = useAssetQuery({
    variables: {
      id: effectiveAssetId || '',
      ...(taskId && { taskId }),
    },
    pause: !effectiveAssetId,
    requestPolicy: 'cache-first', // Use cache-first to prevent background refetches that cause blinking
    // CRITICAL: Add a way to prevent excessive refetches during deployment updates
    context: useMemo(
      () => ({ additionalTypenames: ['Asset', 'Deployment'] }),
      []
    ),
  });

  // Extract the asset from the query result
  const asset = assetData?.asset;

  // Track if we've had a successful load to prevent showing loading state unnecessarily
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Use ref to track if we've initialized background loading to prevent state thrashing
  const backgroundLoadingInitialized = useRef(false);

  // Centralized timer management for all timeouts
  const timersRef = useRef<{
    jobs: NodeJS.Timeout | null;
    history: NodeJS.Timeout | null;
    jobDetection: NodeJS.Timeout[];
  }>({
    jobs: null,
    history: null,
    jobDetection: [],
  });

  // CRITICAL FIX: Add component unmount cleanup to prevent memory leaks
  useEffect(() => {
    isMountedRef.current = true;

    // Capture current ref value for cleanup
    const timers = timersRef.current;

    return () => {
      isMountedRef.current = false;

      // Clear all timers on unmount using captured ref value
      if (timers.jobs) {
        clearTimeout(timers.jobs);
        timers.jobs = null;
      }
      if (timers.history) {
        clearTimeout(timers.history);
        timers.history = null;
      }
      timers.jobDetection.forEach((timeout) => clearTimeout(timeout));
      timers.jobDetection = [];
    };
  }, []); // Empty dependency array - only run on mount/unmount

  // Keep hasLoadedOnce for UI state management (not for background loading)
  useEffect(() => {
    if (asset && !hasLoadedOnce && isMountedRef.current) {
      setHasLoadedOnce(true);
    }
  }, [asset, hasLoadedOnce]);

  // Persist asset name for zero-query mobile BottomSheet titles inside tasks
  useEffect(() => {
    if (!effectiveAssetId) return;
    if (!asset?.name) return;
    try {
      sessionStorage.setItem(`assetName_${effectiveAssetId}`, asset.name);
    } catch {
      // Ignore storage errors (e.g., private mode)
    }
  }, [effectiveAssetId, asset?.name]);

  // Add mutation hook in parent to avoid performance issues in AssetMenu
  const [, updateAsset] = useUpdateAssetMutation();

  // Simple flag to prevent showing "not found" during status updates
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Clear updating status when asset ID changes (navigation) - with proper cleanup
  useEffect(() => {
    // Clean up any pending timers
    if (timersRef.current.jobs) {
      clearTimeout(timersRef.current.jobs);
      timersRef.current.jobs = null;
    }
    if (timersRef.current.history) {
      clearTimeout(timersRef.current.history);
      timersRef.current.history = null;
    }

    if (isMountedRef.current) {
      setHasLoadedOnce(false); // Reset for new asset
      setIsUpdatingStatus(false); // Reset updating state
    }
    backgroundLoadingInitialized.current = false; // Reset background loading flag
    if (isMountedRef.current) {
      setShouldLoadInBackground(false); // Reset background loading state
      setShouldLoadHistoryInBackground(false); // Reset history loading state
      setHighlightedJobId(null); // Clear any highlighted job
      setIsEditingAttributes(false); // Reset attributes editing state
    }
    previousJobsRef.current = []; // Reset jobs tracking for new asset
  }, [effectiveAssetId]); // Only depend on asset ID change

  // CRITICAL FIX: Handler to pass down to AssetMenu for status toggle with mount check
  const handleToggleStatus = useCallback(async () => {
    if (!asset || isUpdatingStatus || !isMountedRef.current) return;

    const newStatus =
      asset.status === Status.Active ? Status.Inactive : Status.Active;

    if (isMountedRef.current) {
      setIsUpdatingStatus(true);
    }

    try {
      await updateAsset({
        id: asset.id,
        input: {
          name: asset.name,
          type: asset.type,
          status: newStatus,
          notes: asset.notes || '',
          images: asset.images || [],
          attachments: asset.attachments?.map((a) => a.id) || [],
          modelId: asset.model?.id ? parseInt(asset.model.id) : null,
        },
      });

      // CRITICAL FIX: Only update state if component is still mounted
      if (isMountedRef.current) {
        setIsUpdatingStatus(false);
      }
    } catch (error) {
      console.error('Failed to update asset status:', error);
      // CRITICAL FIX: Only update state if component is still mounted
      if (isMountedRef.current) {
        setIsUpdatingStatus(false);
      }
    }
  }, [asset, updateAsset, isUpdatingStatus]);

  // Memoize URL parsing to prevent unnecessary re-computations
  const urlParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  // Initialize tab state more reliably to prevent race conditions
  const { getInitialTab, setTabIndex } = useAssetTabState(
    effectiveAssetId || ''
  );
  // PERFORMANCE FIX: For popups, always start at tab 0 (Attributes) to avoid
  // automatically loading expensive History tab data from sessionStorage memory
  const [selectedTabIndex, setSelectedTabIndex] = useState(() =>
    isPopup ? 0 : getInitialTab()
  );
  const [previousTabIndex, setPreviousTabIndex] = useState(0);
  const [isSearchSticked, setIsSearchSticked] = useState(false);
  const [isEditingAttributes, setIsEditingAttributes] = useState(false);

  // Track when background loading should start - use more stable approach
  const [shouldLoadInBackground, setShouldLoadInBackground] = useState(false);
  const [shouldLoadHistoryInBackground, setShouldLoadHistoryInBackground] =
    useState(false);

  // Add scroll handling for sticky search similar to SiteView

  // Navigation tracking removed as it was unused

  // POINT 6 FIX: Simplified and consolidated background loading timer management
  // PERFORMANCE FIX: Disable automatic background loading for popups (task modals)
  // Background loading is an optimization for the main asset view, but causes
  // performance issues in popups where users quickly view and close assets
  useEffect(() => {
    // Skip background loading entirely for popups
    if (isPopup) {
      return;
    }

    // Capture current ref value for cleanup
    const timers = timersRef.current;

    // Clear any existing timers first to prevent race conditions
    if (timers.jobs) {
      clearTimeout(timers.jobs);
      timers.jobs = null;
    }

    // Only start background loading if asset is loaded and component is mounted
    if (
      asset &&
      !fetching &&
      !backgroundLoadingInitialized.current &&
      isMountedRef.current
    ) {
      // Mark as initialized to prevent duplicate triggers
      backgroundLoadingInitialized.current = true;

      // Consolidated timer management: Start both timers in sequence
      timers.jobs = setTimeout(() => {
        if (!isMountedRef.current) return;

        setShouldLoadInBackground(true);
        timers.jobs = null;

        // Chain history timer after jobs timer completes
        const historyTimerId = setTimeout(() => {
          if (!isMountedRef.current) return;
          setShouldLoadHistoryInBackground(true);
          timers.history = null;
        }, TIMING.HISTORY_LOAD_DELAY);
        timers.history = historyTimerId;
      }, TIMING.JOBS_LOAD_DELAY);
    }

    // Cleanup function - use captured ref value
    return () => {
      if (timers.jobs) {
        clearTimeout(timers.jobs);
        timers.jobs = null;
      }
      if (timers.history) {
        clearTimeout(timers.history);
        timers.history = null;
      }
    };
  }, [asset, fetching, isPopup]); // Added isPopup dependency

  // CRITICAL FIX: Improved scroll event listener with better cleanup
  useEffect(() => {
    const scrollContainer = document.querySelector('#asset-view-container');
    if (!scrollContainer) return;

    const containerEdge = isMobile ? 0 : 161;

    const onScroll = () => {
      // Additional check: ensure component is still mounted before state updates
      if (!isMountedRef.current) return;

      const searchEl = scrollContainer.querySelector('#search-attributes');
      if (!searchEl) return;

      const { top } = searchEl.getBoundingClientRect();
      const visible = isMobile ? top === containerEdge : top <= containerEdge;
      setIsSearchSticked(visible);
    };

    scrollContainer.addEventListener('scroll', onScroll);

    // CRITICAL FIX: Store reference to container for cleanup
    const cleanupContainer = scrollContainer;

    return () => {
      // Use stored reference to ensure cleanup even if DOM changes
      cleanupContainer.removeEventListener('scroll', onScroll);
    };
  }, [isMobile]);

  // Memoize deployment data - use raw deployment data since our query returns inline fields
  const deployment = useMemo(
    () => asset?.deployment || null,
    [asset?.deployment]
  );

  // Determine if we're in external user context (sharing permissions provided)
  // When sharingPermissions is provided, we filter tabs based on what's allowed
  // When undefined, all tabs are visible (internal user or direct asset access)
  const isExternalContext = sharingPermissions !== undefined;

  // Build tab configuration based on sharing permissions
  // MOVED EARLIER: Need this before jobsEnabled/historyEnabled calculations
  // Each tab has a key, label, and whether it's visible
  const tabConfig = useMemo(
    () => buildAssetViewTabs(sharingPermissions),
    [sharingPermissions]
  );

  // Get visible tabs only
  const visibleTabs = useMemo(
    () => tabConfig.filter((tab) => tab.visible),
    [tabConfig]
  );

  // Map selected tab index to actual tab key for rendering correct content
  // MOVED EARLIER: Need this before jobsEnabled/historyEnabled calculations
  const selectedTabKey = useMemo(
    () => visibleTabs[selectedTabIndex]?.key || 'attributes',
    [visibleTabs, selectedTabIndex]
  );

  // Memoize loading conditions to prevent unnecessary re-computations
  // PERFORMANCE: For popups, shouldLoadInBackground and shouldLoadHistoryInBackground stay false,
  // so queries only execute when user explicitly clicks on the respective tabs.
  // For main asset view, background loading optimizes perceived performance.
  // CRITICAL FIX: Use selectedTabKey instead of hardcoded indices because tab positions
  // change based on sharing permissions (external users don't see Jobs tab, so History
  // moves from index 2 to index 1)
  const jobsEnabled = useMemo(
    () => shouldLoadInBackground || selectedTabKey === 'jobs',
    [shouldLoadInBackground, selectedTabKey]
  );

  const historyEnabled = useMemo(
    () => shouldLoadHistoryInBackground || selectedTabKey === 'history',
    [shouldLoadHistoryInBackground, selectedTabKey]
  );

  const [jobs, , jobsLoading] = useAssetJobs(asset?.id || '', jobsEnabled);

  // Memoize tab labels from visible tabs
  const tabLabels = useMemo(
    () => visibleTabs.map((tab) => tab.label),
    [visibleTabs]
  );

  // Determine placeholder to show when asset has no image based on asset type
  const imagePlaceholder = useMemo(() => {
    const hasImage = asset?.image || deployment?.image;
    if (hasImage) return undefined;

    // Use asset type-specific placeholder
    return renderAssetTypePlaceholder(asset?.type);
  }, [asset?.image, asset?.type, deployment?.image]);

  // FINAL FIX: Make handleTabChange completely stable to prevent unnecessary re-creations
  const handleTabChange = useCallback(
    (index: number) => {
      if (!isMountedRef.current) return;

      // Use functional updates to avoid dependency on current state
      setPreviousTabIndex((currentSelected) => {
        setSelectedTabIndex(index);
        return currentSelected;
      });

      // Use our sessionStorage-backed tab state management
      setTabIndex(index);
    },
    [setTabIndex] // Only depend on setTabIndex from our hook
  );

  // Memoize slide direction calculation
  const slideDirection = useMemo(
    () => (selectedTabIndex > previousTabIndex ? 1 : -1),
    [selectedTabIndex, previousTabIndex]
  );

  // Reset tab when asset changes, but respect URL parameter
  // CRITICAL FIX: Only reset tab when asset ID actually changes, not when navigating to sub-routes
  // This preserves the active tab when navigating from job tab to task view
  const prevAssetIdRef = useRef<string | undefined>(undefined);
  const prevPathnameRef = useRef<string>(location.pathname);
  const prevSearchRef = useRef<string>(location.search);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Extract shared job highlight logic to avoid duplication
  // This helper handles highlighting a job from URL parameter and setting up cleanup timeout
  const handleJobHighlightFromUrl = useCallback(
    (
      highlightJobId: string,
      currentTabIndex: number,
      jobsTabIndex: number
    ): NodeJS.Timeout | null => {
      if (currentTabIndex !== jobsTabIndex) {
        return null;
      }

      setHighlightedJobId(highlightJobId);

      // Clear highlight after timeout and remove URL parameter
      const highlightTimeout = setTimeout(() => {
        if (isMountedRef.current) {
          setHighlightedJobId(null);
          // Remove the highlight parameter from URL
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('highlightJob');
          window.history.replaceState({}, '', newUrl.toString());
        }
        highlightTimeoutRef.current = null;
      }, TIMING.HIGHLIGHT_DURATION);

      return highlightTimeout;
    },
    []
  );

  useEffect(() => {
    if (!isMountedRef.current) return;

    // Capture ref values at effect start to use in cleanup
    const timers = timersRef.current;

    const assetIdChanged = prevAssetIdRef.current !== effectiveAssetId;
    const searchChanged = prevSearchRef.current !== location.search;

    // Update refs for next comparison
    prevAssetIdRef.current = effectiveAssetId;
    prevPathnameRef.current = location.pathname;
    prevSearchRef.current = location.search;

    // Cleanup previous highlight timeout if it exists
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
      const index = timers.jobDetection.indexOf(highlightTimeoutRef.current);
      if (index > -1) {
        timers.jobDetection.splice(index, 1);
      }
      highlightTimeoutRef.current = null;
    }

    // Check if we're in a sub-route (jobs, tasks, etc.)
    const isSubRoute =
      location.pathname.includes('/jobs/') ||
      location.pathname.includes('/tasks/') ||
      location.pathname.includes('/deployments') ||
      location.pathname.includes('/attachments');

    // Only reset tab if asset changed OR we're on the main asset page and search params changed
    // If we're in a sub-route and asset hasn't changed, don't touch the tab (preserve current state)
    if (assetIdChanged) {
      // Asset changed: reset to URL tab or default
      const tabFromUrl = parseInt(urlParams.get('tab') || '0', 10);
      setPreviousTabIndex(0);
      setSelectedTabIndex(tabFromUrl);
    } else if (!isSubRoute && searchChanged) {
      // Same asset, main page, search params changed: respect URL parameter
      const tabFromUrl = parseInt(urlParams.get('tab') || '0', 10);
      setSelectedTabIndex(tabFromUrl);
    }
    // If same asset and sub-route: do nothing - preserve current tab state

    // Check for job highlight parameter (only if search params changed)
    const highlightJobId = urlParams.get('highlightJob');
    if (highlightJobId && (assetIdChanged || searchChanged)) {
      const jobsTabIndex = visibleTabs.findIndex((tab) => tab.key === 'jobs');
      if (jobsTabIndex !== -1) {
        // Determine current tab index for highlight check
        const storedTab = sessionStorage.getItem(
          `assetTab_${effectiveAssetId}`
        );
        const currentTabIndex = storedTab
          ? parseInt(storedTab, 10)
          : assetIdChanged
          ? parseInt(urlParams.get('tab') || '0', 10)
          : 0;

        const highlightTimeout = handleJobHighlightFromUrl(
          highlightJobId,
          currentTabIndex,
          jobsTabIndex
        );

        if (highlightTimeout) {
          // Store timeout for cleanup
          highlightTimeoutRef.current = highlightTimeout;
          timers.jobDetection.push(highlightTimeout);
        }
      }
    }

    // Cleanup function: clear timeout if effect re-runs or component unmounts
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        const index = timers.jobDetection.indexOf(highlightTimeoutRef.current);
        if (index > -1) {
          timers.jobDetection.splice(index, 1);
        }
        highlightTimeoutRef.current = null;
      }
    };
  }, [
    effectiveAssetId,
    location.pathname,
    location.search,
    urlParams,
    visibleTabs,
    handleJobHighlightFromUrl,
  ]); // urlParams is memoized from location.search, but needed for .get() calls

  // Separate effect to handle URL parameter changes without resetting tab
  const urlHighlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!isMountedRef.current) return;

    // Capture ref value at effect start for cleanup
    const timers = timersRef.current;

    // Cleanup previous timeout if it exists
    if (urlHighlightTimeoutRef.current) {
      clearTimeout(urlHighlightTimeoutRef.current);
      const index = timers.jobDetection.indexOf(urlHighlightTimeoutRef.current);
      if (index > -1) {
        timers.jobDetection.splice(index, 1);
      }
      urlHighlightTimeoutRef.current = null;
    }

    // Only update tab if we're on the main asset view (not a sub-route)
    if (location.pathname.endsWith(effectiveAssetId || '')) {
      const tabFromUrl = parseInt(urlParams.get('tab') || '0', 10);
      setSelectedTabIndex(tabFromUrl);

      // Check for job highlight parameter
      const highlightJobId = urlParams.get('highlightJob');
      const jobsTabIndex = visibleTabs.findIndex((tab) => tab.key === 'jobs');
      if (
        highlightJobId &&
        jobsTabIndex !== -1 &&
        tabFromUrl === jobsTabIndex
      ) {
        const highlightTimeout = handleJobHighlightFromUrl(
          highlightJobId,
          tabFromUrl,
          jobsTabIndex
        );

        if (highlightTimeout) {
          urlHighlightTimeoutRef.current = highlightTimeout;
          timers.jobDetection.push(highlightTimeout);
        }
      }
    }

    // Cleanup function: only clear timeout managed by this effect
    return () => {
      if (urlHighlightTimeoutRef.current) {
        clearTimeout(urlHighlightTimeoutRef.current);
        const index = timers.jobDetection.indexOf(
          urlHighlightTimeoutRef.current
        );
        if (index > -1) {
          timers.jobDetection.splice(index, 1);
        }
        urlHighlightTimeoutRef.current = null;
      }
    };
  }, [
    urlParams,
    location.pathname,
    effectiveAssetId,
    visibleTabs,
    handleJobHighlightFromUrl,
  ]);

  // Track jobs to detect when new job is created
  const previousJobsRef = useRef<typeof jobs>([]);
  const [highlightedJobId, setHighlightedJobId] = useState<string | null>(null);

  // Reset job tracking when asset changes
  useEffect(() => {
    previousJobsRef.current = [];
    if (isMountedRef.current) {
      setHighlightedJobId(null);
    }

    // Clear any pending job detection timeouts
    timersRef.current.jobDetection.forEach((timeout) => clearTimeout(timeout));
    timersRef.current.jobDetection = [];
  }, [asset?.id]);

  // Simplified job tracking for highlighting (URL-based navigation handles tab switching)
  // Track timeout created by this effect separately to avoid clearing other effects' timeouts
  const jobTrackingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    // Only process if we have jobs, asset, and component is mounted
    if (!jobs || !asset || !isMountedRef.current) {
      return;
    }

    // Capture ref value at effect start for cleanup
    const timers = timersRef.current;
    const previousJobs = previousJobsRef.current;

    // Skip if this is the initial load (no previous jobs stored yet)
    if (previousJobs.length === 0) {
      previousJobsRef.current = [...jobs];
      return;
    }

    // Find newly created job by comparing job IDs
    const newJob = jobs.find(
      (job) => !previousJobs.some((prevJob) => prevJob.id === job.id)
    );

    // Early return if no new job found
    if (!newJob) {
      previousJobsRef.current = [...jobs];
      return;
    }

    // Verify that this job actually belongs to the current asset
    const jobBelongsToCurrentAsset =
      newJob.location?.id === asset.id ||
      (deployment && newJob.location?.id === deployment.location?.id);

    if (!jobBelongsToCurrentAsset) {
      previousJobsRef.current = [...jobs];
      return;
    }

    // Only highlight the new job if we're already on the jobs tab (tab switching handled by URL)
    if (selectedTabKey === 'jobs') {
      setHighlightedJobId(newJob.id);

      // Clear any existing timeout from this effect first
      if (jobTrackingTimeoutRef.current) {
        clearTimeout(jobTrackingTimeoutRef.current);
        const index = timers.jobDetection.indexOf(
          jobTrackingTimeoutRef.current
        );
        if (index > -1) {
          timers.jobDetection.splice(index, 1);
        }
        jobTrackingTimeoutRef.current = null;
      }

      // Clear highlight after timeout
      const highlightTimeout = setTimeout(() => {
        if (isMountedRef.current) {
          setHighlightedJobId(null);
        }
        jobTrackingTimeoutRef.current = null;
      }, TIMING.HIGHLIGHT_DURATION);
      jobTrackingTimeoutRef.current = highlightTimeout;
      timers.jobDetection.push(highlightTimeout);
    }

    // Update the ref with current jobs
    previousJobsRef.current = [...jobs];

    // Cleanup function - only clear timeout managed by this effect
    return () => {
      if (jobTrackingTimeoutRef.current) {
        clearTimeout(jobTrackingTimeoutRef.current);
        const index = timers.jobDetection.indexOf(
          jobTrackingTimeoutRef.current
        );
        if (index > -1) {
          timers.jobDetection.splice(index, 1);
        }
        jobTrackingTimeoutRef.current = null;
      }
    };
  }, [jobs, asset?.id, deployment?.location?.id, selectedTabKey]); // Use selectedTabKey instead of selectedTabIndex

  // POINT 4 FIX: Optimize highlighted job clearing to avoid unnecessary effects
  useEffect(() => {
    if (selectedTabKey !== 'jobs' && highlightedJobId && isMountedRef.current) {
      setHighlightedJobId(null);
    }
  }, [selectedTabKey, highlightedJobId]); // Use selectedTabKey instead of selectedTabIndex

  // Callback to clear highlighted job when clicked (with delay)
  const handleHighlightedJobClick = useCallback(() => {
    if (isMountedRef.current) {
      setHighlightedJobId(null);
    }
  }, []);

  // Memoize asset fields to prevent recreation on every render
  const assetFields = useMemo(
    () => [
      ...(asset?.model?.displayName
        ? [
            {
              label: 'Model',
              value: asset?.model?.displayName,
            },
          ]
        : []),

      ...(deployment?.displayLocation
        ? [
            {
              label: 'Location',
              value: deployment?.displayLocation,
            },
          ]
        : []),
      {
        label: 'Asset Type',
        value: asset?.type || '',
      },
    ],
    [asset?.model?.displayName, asset?.type, deployment?.displayLocation]
  );

  // Memoized floating action button actions
  const jobActions = useMemo(
    () => [
      {
        label: 'Job',
        icon: <FontAwesomeIcon icon={faPlus as IconProp} className='h-5 w-5' />,
        onClick: () => {
          // Preserve tab parameter when navigating to job creation
          const searchParams = new URLSearchParams(location.search);
          const tabParam = searchParams.get('tab');
          const targetPath = tabParam ? `jobs/new?tab=${tabParam}` : 'jobs/new';
          navigate(targetPath);
        },
      },
    ],
    [navigate, location.search]
  );

  // Dynamic actions based on selected tab
  const currentActions = useMemo(() => {
    // All tabs use job actions
    return jobActions;
  }, [jobActions]);

  // Don't call reload() - individual queries handle cache updates automatically
  const handleAttributeUpdate = useCallback(() => {
    // GraphQL cache will automatically update when attributes change
    // No action needed as cache updates automatically
  }, []);

  // Memoize the close handler to prevent recreation on every render
  const handleClose = useCallback(() => {
    navigate(siteId ? '../..' : '/assets');
  }, [navigate, siteId]);

  const tabIndicatorStyle = useMemo(
    () => ({
      width: tabLabels.length > 0 ? `${100 / tabLabels.length}%` : '0%',
      height: '100%',
    }),
    [tabLabels.length]
  );

  // Only show loading state on initial load or when we don't have an asset yet
  if (!asset) {
    // Don't show "not found" if we're updating status (asset might be temporarily unavailable)
    if (isUpdatingStatus) {
      return <AssetViewSkeleton isPopup={isPopup} />;
    }

    // Only show loading if we're actually fetching and have never loaded this asset before
    if (fetching && !hasLoadedOnce) {
      return <AssetViewSkeleton isPopup={isPopup} />;
    }

    // If we're not fetching and have no asset, it's not found
    if (!fetching && hasLoadedOnce) {
      return (
        <SideLayout isPopup={isPopup}>
          <SideLayout.Head>Asset Not Found</SideLayout.Head>
          <SideLayout.Body className='flex items-center justify-center text-gray-500'>
            Asset not found or has been deleted.
          </SideLayout.Body>
        </SideLayout>
      );
    }

    // Fallback loading state
    return <AssetViewSkeleton isPopup={isPopup} />;
  }

  // Asset is already available, no need for redundant assignment

  return (
    <div
      className={classNames(
        'absolute h-full w-full lg:relative lg:w-auto',
        matchSites || matchAssets
          ? 'block'
          : 'flex h-full items-stretch justify-start'
      )}
    >
      <SideLayout
        willOverflow
        isPopup={isPopup}
        id='asset-view-container'
        data-fab-container='true'
        className='h-full'
      >
        {!isPopup && (
          <SideLayout.Head
            rightSlot={
              <AssetMenu asset={asset} onToggleStatus={handleToggleStatus} />
            }
            onClose={handleClose}
          >
            {asset.name}
            {asset.status !== Status.Active && (
              <div className='ml-2 h-3 w-3 rounded-full bg-red-500' />
            )}
          </SideLayout.Head>
        )}

        <SideLayout.Body>
          <div
            data-usetiful='asset-view'
            className={classNames('flex h-full flex-col', isPopup && 'flex-1')}
          >
            <InfoCard
              fields={assetFields}
              image={asset.image || deployment?.image || undefined}
              imagePlaceholder={imagePlaceholder}
            ></InfoCard>
            <div className='mt-4'>
              <ReadValue label='notes' value={asset.notes} />
            </div>
            <div className='mb-4 mt-4 flex flex-1 flex-col'>
              <div
                id='asset-tabs-container'
                className='relative flex flex-1 flex-col rounded-lg border border-outline-variant'
              >
                {visibleTabs.length > 0 ? (
                  <TabGroup
                    className='flex flex-1 flex-col rounded-lg'
                    selectedIndex={selectedTabIndex}
                    onChange={handleTabChange}
                  >
                    <div className='bg-blue-900/20 relative overflow-hidden rounded-t-lg'>
                      <TabList className='relative z-10 flex'>
                        {tabLabels.map((label) => (
                          <TabHeader key={label} title={label} />
                        ))}
                      </TabList>

                      {/* Sliding active indicator */}
                      <motion.div
                        className='absolute bottom-0 left-0 border border-brand bg-brand shadow'
                        style={tabIndicatorStyle}
                        animate={{
                          x: `${selectedTabIndex * 100}%`,
                        }}
                        transition={{
                          duration: 0.2,
                          ease: 'easeInOut',
                        }}
                      />
                    </div>

                    <TabPanels className='relative flex flex-1 flex-col overflow-hidden rounded-b-lg bg-surface-container-background'>
                      <AnimatePresence mode='wait'>
                        <motion.div
                          key={selectedTabKey}
                          initial={{
                            x: slideDirection * 20,
                            opacity: 0,
                          }}
                          animate={{
                            x: 0,
                            opacity: 1,
                          }}
                          exit={{
                            x: slideDirection * -20,
                            opacity: 0,
                          }}
                          transition={{
                            duration: 0.15,
                            ease: 'easeInOut',
                          }}
                          className='flex flex-1 flex-col'
                        >
                          {/* Render tab content based on selected tab key, not index */}
                          {/* This allows tabs to be filtered based on sharing permissions */}
                          {selectedTabKey === 'attributes' && (
                            <TabPanel
                              className='relative flex flex-1 flex-col bg-surface-container-background'
                              static
                            >
                              <div className='flex-1 overflow-auto pb-20'>
                                <AttributesList
                                  isPopup={isPopup}
                                  formParent={'asset'}
                                  attributes={asset.attributes as any}
                                  onUpdate={handleAttributeUpdate}
                                  entityId={`Asset:${asset.id}`}
                                  entityTypeName={t('Asset')}
                                  isSearchSticked={isSearchSticked}
                                  isEditing={isEditingAttributes}
                                  onEditingChange={setIsEditingAttributes}
                                />
                              </div>
                            </TabPanel>
                          )}

                          {selectedTabKey === 'jobs' && (
                            <TabPanel
                              className='relative flex flex-1 flex-col bg-surface-container-background'
                              static
                            >
                              <div className='flex-1 overflow-auto'>
                                <AssetJobs
                                  assetId={asset.id}
                                  highlightedJobId={highlightedJobId}
                                  showSkeleton={jobsLoading && !jobs.length}
                                  onHighlightedJobClick={
                                    handleHighlightedJobClick
                                  }
                                />
                              </div>
                            </TabPanel>
                          )}

                          {selectedTabKey === 'history' && (
                            <TabPanel
                              className='relative flex flex-1 flex-col bg-surface-container-background'
                              static
                            >
                              <div className='flex-1 overflow-auto'>
                                <AssetHistory
                                  assetId={asset.id}
                                  jobId={jobId}
                                  taskId={taskId}
                                  // Show skeleton when queries are paused (enabled=false) to indicate loading state
                                  // This inverse relationship ensures skeleton displays while queries are disabled
                                  showSkeleton={!historyEnabled}
                                  // Control query execution: when false, queries are paused to avoid unnecessary network requests
                                  enabled={historyEnabled}
                                  // Pass sharing permissions to filter history content for external users
                                  sharingPermissions={
                                    isExternalContext
                                      ? sharingPermissions
                                      : undefined
                                  }
                                />
                              </div>
                            </TabPanel>
                          )}
                        </motion.div>
                      </AnimatePresence>
                    </TabPanels>
                  </TabGroup>
                ) : (
                  <div className='flex flex-1 items-center justify-center px-4 py-8 text-sm text-tertiary'>
                    {isExternalContext
                      ? t(
                          'This asset has no shared content configured for external viewers.'
                        )
                      : t('No tabs are available for this asset.')}
                  </div>
                )}

                {/* Show FloatingActionButton when not in popup, not editing attributes on attributes tab, and not in external context */}
                {/* FAB shows + job or redeploy based on selected tab, even in site context */}
                {/* FAB positioned relative to asset tabs container */}
                {!isPopup &&
                  !isExternalContext &&
                  !(isEditingAttributes && selectedTabKey === 'attributes') && (
                    <FloatingActionButton
                      dataUsetiful={currentActions
                        .map((action) => `create-${action.label}`)
                        .join(',')}
                      inTabsContext={true}
                      targetSelector='#asset-tabs-container'
                      hierarchy='primary'
                      actions={currentActions}
                      key='asset-fab'
                      className='transition-all duration-200 ease-in-out'
                    />
                  )}
              </div>
            </div>
          </div>
        </SideLayout.Body>
      </SideLayout>
    </div>
  );
}
